import { memo, useCallback, useState } from 'react';

import { useHitlGateDecision } from '../../hooks/useHitlGateDecision.js';
import HitlGateCardShell from './hitlGateCard/HitlGateCardShell.jsx';
import ApprovalQaGateBody from './hitlGateCard/ApprovalQaGateBody.jsx';
import ChoiceGateBody from './hitlGateCard/ChoiceGateBody.jsx';
import PickGateBody from './hitlGateCard/PickGateBody.jsx';

/**
 * In-chat HITL gate approval card.
 *
 * Renders the same approval decision the My Tasks inbox shows, inline in the
 * conversation, when the run emits `chat.response.input.required`. Approve /
 * Reject resolve through the shared {@link useHitlGateDecision} core against
 * the same `/v1/tasks/hitl/resolve` endpoint — the durable inbox task remains
 * the fallback, so a missed WS event or a dismissed card never loses the
 * decision.
 *
 * The card is ephemeral: it clears on resolve, on an "already resolved"
 * conflict, and whenever the host clears `hitlRequest` (conversation switch).
 *
 * Composition (one concern per file):
 * - {@link HitlGateCardShell} — Card frame + identifying header
 *   (collapse/dismiss/feature title/raised-at).
 * - {@link useHitlGateDecision} — shared resolve/dismiss/toast core.
 * - {@link ApprovalQaGateBody} / {@link ChoiceGateBody} / {@link PickGateBody}
 *   — one body per gate mode, each owning only its own state.
 *
 * @param {object} props
 * @param {{ interactionId: string, render: object, gate?: string, mode?: string, createdAt?: string }} props.card
 *   Normalized view-model from `mapInputRequiredToGateCard`.
 * @param {import('axios').AxiosInstance} props.apiClient
 * @param {() => void} props.onResolved  Clears the slot (e.g. `setHitlRequest(null)`).
 * @param {(() => void)|null} [props.onOpenTasks]  Host navigation to My Tasks;
 *   the link is hidden when not provided (embed-safe — no hardcoded route).
 * @param {((card: object) => void)|null} [props.onPickDocument]  Host opens the
 *   AIMS Drive picker for a `mode: 'pick'` gate and resolves it with the chosen
 *   `document_uuid`. Required for document-pick gates to be actionable in chat.
 * @param {((card: object, file: File) => Promise<boolean>)|null} [props.onAttachLocalFile]
 *   Host uploads a local file through the composer pipeline and resolves the
 *   `mode: 'pick'` gate with the resulting `file_uuid`. When omitted the local
 *   upload control is hidden (Drive-only).
 * @param {(() => void)|null} [props.onResumeStart]  Called only when a genuine
 *   decision resumes the workflow (approve / reject / send-answer / continue /
 *   submit). The resume emits no lifecycle events, so the host uses this to show
 *   a "working" indicator until the next gate / completion arrives. Fired BEFORE
 *   the resolve round-trip (LangGraph resolves synchronously with the resumed
 *   run, so its terminal events clear the indicator during the await). NOT
 *   called on manual dismiss or already-resolved / resume-conflict.
 * @param {(() => void)|null} [props.onResumeEnd]  Called when a decision FAILED
 *   to resume (invalid input, stale card, retryable error) so the host can stop
 *   the optimistic working indicator.
 * @param {string} [props.translationNamespace='chat']
 */
function HitlGateCard({
  card,
  apiClient,
  onResolved,
  onOpenTasks = null,
  onPickDocument = null,
  onAttachLocalFile = null,
  onResumeStart = null,
  onResumeEnd = null,
  translationNamespace = 'chat',
}) {
  // Collapse the body (Claude/Cursor-style) so a docked card can shrink to just
  // its header while a long thread or another card needs the room.
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);

  const decision = useHitlGateDecision({
    apiClient,
    card,
    onResolved,
    onResumeStart,
    onResumeEnd,
    translationNamespace,
  });

  const isPick = card.mode === 'pick';
  const isChoice = card.mode === 'choice';

  return (
    <HitlGateCardShell
      card={card}
      resolving={decision.resolving}
      collapsed={collapsed}
      onToggleCollapse={toggleCollapsed}
      onDismiss={decision.dismiss}
      gapClass={isChoice ? 'gap-4' : 'gap-3'}
      translationNamespace={translationNamespace}
    >
      {isPick ? (
        <PickGateBody
          card={card}
          decision={decision}
          onPickDocument={onPickDocument}
          onAttachLocalFile={onAttachLocalFile}
          onOpenTasks={onOpenTasks}
          translationNamespace={translationNamespace}
        />
      ) : isChoice ? (
        <ChoiceGateBody
          card={card}
          decision={decision}
          onOpenTasks={onOpenTasks}
          translationNamespace={translationNamespace}
        />
      ) : (
        <ApprovalQaGateBody
          card={card}
          decision={decision}
          onOpenTasks={onOpenTasks}
          translationNamespace={translationNamespace}
        />
      )}
    </HitlGateCardShell>
  );
}

export default memo(HitlGateCard);
